import React, {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { isPlayAllAnimationsFeatureEnabled, useGifPlaybackToggle } from './gifPlaybackUtils';

const defaultContextValue = {
    isPlaying: true,
    hasActiveGifs: false,
    activeGifCount: 0,
    registerGif: () => {},
    unregisterGif: () => {},
    playAll: () => {},
    pauseAll: () => {}
};

const GifPlaybackContext = createContext(defaultContextValue);

export const GifPlaybackProvider = ({ children }) => {
    const { isPlaying, play, pause } = useGifPlaybackToggle(isPlayAllAnimationsFeatureEnabled());
    const [activeGifCount, setActiveGifCount] = useState(0);
    const hasActiveGifs = activeGifCount > 0;

    
    const userPausedRef = useRef(false);

    const registerGif = useCallback(() => {
        setActiveGifCount(count => count + 1);
    }, []);

    const unregisterGif = useCallback(() => {
        setActiveGifCount(count => Math.max(0, count - 1));
    }, []);

    const playAll = useCallback(() => {
        userPausedRef.current = false;
        play();
    }, [play]);

    const pauseAll = useCallback(() => {
        userPausedRef.current = true;
        pause();
    }, [pause]);

    
    useEffect(() => {
        if (hasActiveGifs && !userPausedRef.current) {
            play();
        }
    }, [hasActiveGifs, play]);

    const contextValue = useMemo(() => ({
        isPlaying,
        hasActiveGifs,
        activeGifCount,
        registerGif,
        unregisterGif,
        playAll,
        pauseAll
    }), [isPlaying, hasActiveGifs, activeGifCount, registerGif, unregisterGif, playAll, pauseAll]);

    return (
        <GifPlaybackContext.Provider value={contextValue}>
            {children}
        </GifPlaybackContext.Provider>
    );
};

GifPlaybackProvider.propTypes = {
    children: PropTypes.node.isRequired
};

export const useGifPlayback = () => useContext(GifPlaybackContext);
